import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, Tag, Trash2, Plus, RotateCcw } from 'lucide-react';

const SettingsPhotoshootSlots = () => {
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const API_BASE_URL = '/api';

    useEffect(() => {
        loadSlots();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSlots = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('admin_token');
            const { data } = await axios.get(`${API_BASE_URL}/slots/admin`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (data.success) {
                // Map DB keys to frontend keys
                const mappedSlots = (data.slots || []).map(slot => ({
                    ...slot,
                    start: slot.start_time, // Backend returns start_time, UI needs start
                    end: slot.end_time      // Backend returns end_time, UI needs end
                }));
                setSlots(mappedSlots);
            }
        } catch (error) {
            console.error('Error loading slots:', error);
            // Fallback empty if fail
        } finally {
            setLoading(false);
        }
    };


    // New Wrapper for saving all (iterates since API is per-slot or bulk?)
    // My API is per-slot upsert. Let's change this UI to save individually or loop.
    // The previous UI had a "Save Changes" button for ALL.
    // Efficient way: Loop and save all modified.
    // For now, let's keep the "Save All" button but make it loop requests (simple) or add bulk endpoint (better).
    // Given the previous code just upserted one by one in my "admin" endpoint? 
    // Wait, my slots.js "POST /admin" takes a SINGLE slot object.
    // So "Save Changes" button needs to loop.

    const saveAll = async () => {
        try {
            const token = localStorage.getItem('admin_token');
            // Parallel requests
            await Promise.all(slots.map(slot =>
                axios.post(`${API_BASE_URL}/slots/admin`, slot, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                })
            ));
            alert('All slots saved successfully!');
            loadSlots();
        } catch (e) {
            console.error('Save error:', e);
            alert('Failed to save some slots');
        }
    };

    const reset = async () => {
        alert("Resetting to defaults is now handled via database seeds. Please contact support if you need to restore factory defaults.");
    };

    const addSlot = () => {
        const newSlot = {
            id: `SLOT_${Date.now()}`,
            start: '09:00',
            end: '12:00',
            label: 'New Slot',
            duration: 3,
            price: 5000,
            deposit: 2000,
            active: true
        };
        setSlots([...slots, newSlot]);
        // Optional: auto-save immediately so it exists? Or wait for "Save Changes"?
        // Wait for save changes is safer UI pattern.
    };

    const updateSlot = (index, field, value) => {
        const updated = [...slots];
        updated[index] = { ...updated[index], [field]: value };

        // Auto-calculate duration
        if (field === 'start' || field === 'end') {
            const start = field === 'start' ? value : updated[index].start;
            const end = field === 'end' ? value : updated[index].end;

            if (start && end) {
                const [startH, startM] = start.split(':').map(Number);
                const [endH, endM] = end.split(':').map(Number);
                let duration = Math.round((endH * 60 + endM - startH * 60 - startM) / 60 * 10) / 10;
                if (duration < 0) duration += 24; // Handle overnight? (Simple logic for now)

                updated[index].duration = duration;
            }
        }
        setSlots(updated);
    };

    const deleteSlot = async (index, slotId) => {
        if (!window.confirm('Delete this slot? This action is permanent.')) return;

        // If it's a new unsaved slot (only in local state), just remove it
        // We can check if it exists in DB by checking if we fetched it? 
        // Or just try to delete from DB and ignore 404.

        try {
            const token = localStorage.getItem('admin_token');
            await axios.delete(`${API_BASE_URL}/slots/admin/${slotId}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            // Update local state
            const updated = [...slots];
            updated.splice(index, 1);
            setSlots(updated);

        } catch (e) {
            console.error('Delete error', e);
            alert('Failed to delete slot (it might be in use)');
        }
    };

    if (loading) return <div className="text-gray-500 p-4">Loading configuration...</div>;

    return (
        <div className="space-y-8">
            {/* Header Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 bg-blue-100 rounded-full p-2">
                        <Clock className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-blue-900">Configurable Slot System</h3>
                        <p className="mt-1 text-sm text-blue-700 leading-relaxed max-w-2xl">
                            Define your photoshoot slots here. You can set the time ranges, display labels, pricing, and active status.
                            Changes made here update the booking calendar immediately.
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Editor */}
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-8 py-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
                    <div>
                        <h4 className="text-xl font-bold text-gray-800">Available Slots</h4>
                        <p className="text-sm text-gray-500 mt-1">Configure {slots.length} photoshoot timing options</p>
                    </div>
                    <button
                        onClick={addSlot}
                        className="px-6 py-2.5 rounded-xl text-white font-bold shadow-lg shadow-[#471F21]/20 hover:shadow-xl flex items-center gap-2 transform active:scale-95 transition-all text-sm"
                        style={{ backgroundColor: '#471F21' }}
                    >
                        <Plus className="w-5 h-5" />
                        Add New Slot
                    </button>
                </div>

                <div className="p-8">
                    {/* Header Row - Hidden on mobile */}
                    <div className="hidden md:grid grid-cols-12 gap-6 text-[10px] font-bold text-gray-400 uppercase tracking-[0.1em] px-4 pb-4 border-b border-gray-50">
                        <div className="col-span-1">ID</div>
                        <div className="col-span-3">Time Window</div>
                        <div className="col-span-4">Display Label & Active</div>
                        <div className="col-span-3">Pricing Details</div>
                        <div className="col-span-1 text-right">Actions</div>
                    </div>

                    {/* Slot Rows */}
                    <div className="divide-y divide-gray-50">
                        {slots.map((slot, i) => (
                            <div key={slot.id || i} className="group grid grid-cols-1 md:grid-cols-12 gap-6 items-center py-6 px-4 hover:bg-gray-50/50 transition-all rounded-xl mt-2">

                                {/* ID Field */}
                                <div className="col-span-1">
                                    <span className="md:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 block">ID</span>
                                    <input
                                        className="w-full bg-white border border-gray-200 text-gray-700 text-sm font-bold rounded-xl px-3 py-2.5 focus:border-[#471F21] focus:ring-0 outline-none transition-all"
                                        value={slot.id}
                                        onChange={e => updateSlot(i, 'id', e.target.value)}
                                        placeholder="ID"
                                    />
                                </div>

                                {/* Time Range */}
                                <div className="col-span-3">
                                    <span className="md:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 block">Time</span>
                                    <div className="flex items-center gap-3 bg-white p-2 rounded-xl border border-gray-200 shadow-sm">
                                        <div className="relative flex-1">
                                            <input
                                                type="time"
                                                className="w-full bg-transparent border-none text-xs font-bold focus:ring-0 p-0 text-gray-700"
                                                value={slot.start}
                                                onChange={e => updateSlot(i, 'start', e.target.value)}
                                            />
                                        </div>
                                        <span className="text-gray-300 font-bold text-[10px]">TO</span>
                                        <div className="relative flex-1">
                                            <input
                                                type="time"
                                                className="w-full bg-transparent border-none text-xs font-bold focus:ring-0 p-0 text-gray-700 text-right"
                                                value={slot.end}
                                                onChange={e => updateSlot(i, 'end', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Label & Active */}
                                <div className="col-span-4">
                                    <span className="md:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 block">Label</span>
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1">
                                            <input
                                                className="w-full border-b-2 border-transparent bg-transparent px-1 py-1 text-sm font-semibold text-gray-800 focus:border-[#471F21] outline-none transition-all placeholder-gray-300"
                                                value={slot.label}
                                                onChange={e => updateSlot(i, 'label', e.target.value)}
                                                placeholder="e.g. Afternoon Glow"
                                            />
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-[10px] font-bold text-[#471F21] bg-[#471F21]/5 px-2 py-0.5 rounded-full">
                                                    {slot.duration} HRS
                                                </span>
                                            </div>
                                        </div>
                                        <label className="flex items-center gap-2 cursor-pointer touch-none select-none">
                                            <div
                                                onClick={() => updateSlot(i, 'active', !slot.active)}
                                                className={`w-10 h-5 rounded-full relative transition-all duration-300 ${slot.active !== false ? 'bg-green-500' : 'bg-gray-200'}`}
                                            >
                                                <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 ${slot.active !== false ? 'left-6' : 'left-1'}`}></div>
                                            </div>
                                            <span className={`text-[10px] font-bold tracking-wider ${slot.active !== false ? 'text-green-600' : 'text-gray-400'}`}>
                                                {slot.active !== false ? 'ACTIVE' : 'OFF'}
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                {/* Pricing */}
                                <div className="col-span-3">
                                    <span className="md:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 block">Pricing</span>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">₹</span>
                                            <input
                                                type="number"
                                                className="w-full border border-gray-200 rounded-xl pl-6 pr-3 py-2 text-sm font-bold focus:border-[#471F21] focus:ring-0 outline-none transition-all"
                                                value={slot.price}
                                                onChange={e => updateSlot(i, 'price', Number(e.target.value))}
                                                placeholder="Price"
                                            />
                                        </div>
                                        <div className="relative flex-1">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">₹</span>
                                            <input
                                                type="number"
                                                className="w-full border border-gray-200 rounded-xl pl-6 pr-3 py-2 text-sm font-bold focus:border-[#471F21] focus:ring-0 outline-none transition-all"
                                                value={slot.deposit}
                                                onChange={e => updateSlot(i, 'deposit', Number(e.target.value))}
                                                placeholder="Adv"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="col-span-1 flex justify-end">
                                    <button
                                        onClick={() => deleteSlot(i, slot.id)}
                                        className="p-3 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                        title="Delete Slot"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        ))}

                        {slots.length === 0 && (
                            <div className="text-center py-20">
                                <Tag className="w-12 h-12 text-gray-100 mx-auto mb-4" />
                                <h3 className="text-gray-400 font-semibold tracking-wide">NO SLOTS CONFIGURED</h3>
                                <button onClick={addSlot} className="mt-4 text-[#471F21] text-sm font-bold hover:underline">Create your first slot</button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="bg-gray-50/50 px-8 py-6 flex items-center justify-between border-t border-gray-50">
                    <button
                        onClick={reset}
                        className="flex items-center gap-2 text-xs text-gray-400 hover:text-red-500 font-bold transition-colors"
                    >
                        <RotateCcw className="w-4 h-4" />
                        RESET DEFAULTS
                    </button>
                    <div className="flex gap-4">
                        <button
                            onClick={loadSlots}
                            className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm font-bold hover:bg-white hover:text-gray-800 transition-all"
                        >
                            Discard
                        </button>
                        <button
                            onClick={saveAll}
                            className="px-8 py-2.5 rounded-xl text-white text-sm font-bold shadow-lg shadow-[#471F21]/20 hover:shadow-xl transition-all"
                            style={{ background: '#471F21' }}
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPhotoshootSlots;
